import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Button, Checkbox, DatePicker, Form, message, Popconfirm, Popover, Row, Col, Select, Spin, TimePicker } from 'antd-v5';
import { format, set } from 'date-fns';
import { useQuery } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DescriptionBox from '../../../../components/Layout/DescriptionBox';
import { deleteRequest, getRequest, postRequest, putRequest } from '../../../../core/apiRequests';
import dayjs from 'dayjs';
import objectSupport from 'dayjs/plugin/objectSupport';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { all } from 'axios';

dayjs.extend(objectSupport);
dayjs.extend(customParseFormat);

const daysOfWeek = [
  { label: 'Sunday', day: 'sunday' },
  { label: 'Monday', day: 'monday' },
  { label: 'Tuesday', day: 'tuesday' },
  { label: 'Wednesday', day: 'wednesday' },
  { label: 'Thursday', day: 'thursday' },
  { label: 'Friday', day: 'friday' },
  { label: 'Saturday', day: 'saturday' },
];

const parseHours = (start, end) => {
  const openTime = dayjs({ hour: Math.floor(start / 100), minute: start % 100 });
  const closeTime = dayjs({ hour: Math.floor(end / 100), minute: end % 100 });
  return [openTime, closeTime];
};

export const AssociateHours = ({ associateId, storeOptions }) => {
  const [saving, setSaving] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [form] = Form.useForm();
  const [allWorkhourStores, setAllWorkhourStores] = useState();
  const [availability, setAvailability] = useState({
    date: null,
    startTime: null,
    endTime: null,
    storeId: null,
    closed: false,
  });

  const {
    data: workingHoursData,
    refetch: refetchWorkingHours,

  } = useQuery(
    ['associate-workinghours', associateId],
    async () => {
      try {
        const reqObj = {
          params: ['associates', associateId, 'workinghours'],
          loadingStatusOff: true,
        };
        const response = await getRequest(reqObj);
        setAllWorkhourStores(response?.map(item => item.storeId));
        const assoc_hours = response?.reduce(
          (acc, curr) => {
            daysOfWeek
              .map(item => item.day)
              .forEach(day => {
                if (curr[`${day}Start`] != null && curr[`${day}End`] != null) {
                  acc[day] = {
                    hours: parseHours(curr[`${day}Start`], curr[`${day}End`]),
                    enabled: true,
                    storeId: curr.storeId,
                  };
                }
              });
            return acc;
          },
          daysOfWeek.reduce((acc, curr) => Object.assign(acc, { [curr.day]: null }), {}),
        );
        return assoc_hours;
      } catch (e) {
        return [];
      }
    },
    {
      enabled: !!associateId,
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  const {
    data: availabilityData,
    isFetching: isFetchingAvailability,
    refetch: refetchAvailability,
  } = useQuery(
    ['associate-hoursOverride', associateId],
    async () => {
      try {
        const reqObj = {
          params: ['associates', associateId, 'workinghoursByDate'],
          loadingStatusOff: true,
        };
        const response = await getRequest(reqObj);
        return response.data;
      } catch (e) {
        return [];
      }
    },
    {
      enabled: !!associateId,
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  useEffect(() => {
    if (workingHoursData && Object.keys(workingHoursData).length > 0) {
      form.setFieldsValue({ open_hours: workingHoursData });
    }
  }, [workingHoursData, form]);

  const handleOpenNew = useCallback(() => {
    setOpenAdd(true);
    setAvailability({
      date: null,
      startTime: null,
      endTime: null,
      closed: false,
      id: null,
    });
  }, []);

  const handleEdit = useCallback(item => {
    setOpenAdd(true);
    setAvailability({
      date: dayjs(item.date),
      startTime: item.startTime
        ? dayjs()
          .set('hour', Math.floor(item.startTime / 100))
          .set('minute', item.startTime % 100)
        : null,
      endTime: item.endTime
        ? dayjs()
          .set('hour', Math.floor(item.endTime / 100))
          .set('minute', item.endTime % 100)
        : null,
      closed: !item.startTime && !item.endTime,
      storeId: item.storeId,
    });
  }, []);

  const handleCancel = useCallback(() => {
    setOpenAdd(false);
    setAvailability({
      date: null,
      startTime: null,
      endTime: null,
      closed: false,
      id: null,
    });
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    const data = {
      date: availability.date.format('YYYY-MM-DD'),
      startTime: availability.startTime && !availability.closed ? Number(availability.startTime.format('HHmm')) : null,
      endTime: availability.endTime && !availability.closed ? Number(availability.endTime.format('HHmm')) : null,
      storeId: availability.storeId
    };
    try {
      const reqObj = {
        params: ['associates', Number(associateId), 'workinghoursByDate'],
        data,
        loadingStatusOff: true,
      };
      await postRequest(reqObj);
      message.success('Availability saved');
      setOpenAdd(false);
      setAvailability({
        date: null,
        startTime: null,
        endTime: null,
        closed: false,
        id: null,
      });
      refetchAvailability();
    } catch (e) {
      console.error(e);
      message.error('There was an error saving the availability');
    } finally {
      setSaving(false);
    }
  }, [availability, associateId, refetchAvailability]);

  const handleDelete = useCallback(async (storeId, date) => {
    try {
      setSaving(true);
      const reqObj = {
        params: ['associates', associateId, 'workinghoursByDate'],
        query: { storeId, date: dayjs(date).format('YYYY-MM-DD') },
        loadingStatusOff: true,
      };
      await deleteRequest(reqObj);
      message.success('Availability deleted');
      refetchAvailability();
    } catch (e) {
      console.error(e);
      message.error('There was an error deleting the availability');
    } finally {
      setSaving(false);
    }
  }, [associateId, refetchAvailability]);

  const handleSaveHours = useCallback(async () => {
    try {
      setSaving(true);
      const openHours = form.getFieldValue('open_hours');
      if (Object.values(openHours).find(item => item?.enabled && (!item.storeId || !item.hours))) {
        message.warning('Please select a store and hours for all enabled days before saving.');
        setSaving(false);
        return;
      }
      const storesMap = Object.entries(openHours).reduce((acc, curr) => {
        if (!curr[1]?.enabled) return acc;
        const dayData = {
          day: curr[0],
          start: curr[1]?.enabled && curr[1]?.hours ? curr[1]?.hours[0].format('Hmm') : null,
          end: curr[1]?.enabled && curr[1]?.hours ? curr[1]?.hours[1].format('Hmm') : null,
          storeId: curr[1]?.storeId,
        };
        if (acc[dayData.storeId]) {
          acc[dayData.storeId].push(dayData);
        } else {
          acc[dayData.storeId] = [dayData];
        }
        return acc;
      }, {});
      allWorkhourStores?.forEach(async stId => {
        if (!storesMap[stId]) {
          const reqObj = {
            params: ['associates', associateId, 'workinghours'],
            query: { storeId: stId },
          };
          await deleteRequest(reqObj);
        }
      });
      await Promise.all(Object.keys(storesMap).map(async storeId => {
        const hoursData = storesMap[storeId].reduce((acc, curr) => {
          acc[`${curr.day}Start`] = curr.start ? parseInt(curr.start) : null;
          acc[`${curr.day}End`] = curr.end ? parseInt(curr.end) : null;
          return acc;
        }, {});
        const reqObj = {
          params: ['associates', associateId, 'workinghours'],
          data: {
            hoursData,
            storeId: parseInt(storeId)
          },
          loadingStatusOff: true,
        };
        await postRequest(reqObj);
      }));
      message.success('Working hours updated');
      refetchWorkingHours();

    } catch (e) {
      console.error(e);
      message.error('There was an error deleting the availability');
    } finally {
      setSaving(false);
    }
  }, [form, allWorkhourStores, associateId, refetchWorkingHours]);

  const disabledDates = useMemo(() => {
    if (availabilityData) {
      return availabilityData.map(item => {
        return set(dayjs(item.date).toDate(), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
      });
    }
  }, [availabilityData]);

  return (
    <div className="w-100-P">
      <Form form={form} layout="vertical" style={{ width: '100%' }}>
        {daysOfWeek.map(day => (
          <AssociateHoursItem
            key={day.day}
            label={day.label}
            day={day.day}
            storeOptions={storeOptions}
            form={form}
          />
        ))}
        <div className='flex-row-start-center m-top-16'>
          <Button type="primary" onClick={handleSaveHours}>
            Save
          </Button>
        </div>
        <Spin spinning={isFetchingAvailability}>
          <DescriptionBox
            className="w-100-P m-top-30"
            title="Hours on Specific Dates"
            description="Manage dates that you are not working or have different hours than your normal schedule"
            input={
              <Button disabled={openAdd} type="primary" onClick={handleOpenNew}>
                Add a Date
              </Button>
            }
          />
          {openAdd && (
            <div className="flex-row-spacebetween-center w-100-P m-btm-24">
              <div className="flex-row-center gap-16">
                <DatePicker
                  value={availability.date}
                  onChange={date => setAvailability({ ...availability, date })}
                  disabledDate={current => {
                    return (
                      disabledDates?.some(date => date.getTime() === current.startOf('day').toDate().getTime()) ||
                      current < new Date().setDate(new Date().getDate() - 1)
                    );
                  }}
                />
                <TimePicker.RangePicker
                  disabled={availability.closed}
                  style={{ width: '320px' }}
                  format="hh:mma"
                  value={[availability.startTime, availability.endTime]}
                  onChange={([open, close]) => setAvailability({ ...availability, startTime: open, endTime: close })}
                />
                <Select
                  options={storeOptions}
                  popupMatchSelectWidth={false}
                  style={{ minWidth: '150px' }}
                  placeholder={'Store'}
                  value={availability.storeId}
                  onChange={e => setAvailability({ ...availability, storeId: e })}
                />
                <Checkbox
                  checked={availability.closed}
                  onChange={e => setAvailability({ ...availability, closed: e.target.checked })}
                >
                  Out of Office
                </Checkbox>
              </div>
              <div className="flex-row-center gap-16">
                <Button
                  type="primary"
                  onClick={handleSave}
                  disabled={
                    !availability.date ||
                    (!availability.closed && (!availability.startTime || !availability.endTime)) ||
                    !availability.storeId ||
                    saving
                  }
                  loading={saving}
                >
                  Save
                </Button>
                <Button onClick={handleCancel} disabled={saving}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {availabilityData?.map(item => (
            <AvailabilityItem key={item.id} {...item} handleEdit={handleEdit} handleDelete={handleDelete} storeOptions={storeOptions} />
          ))}
        </Spin>
      </Form>
    </div>
  );
};

const AvailabilityItem = ({ endTime, startTime, date, handleEdit, handleDelete, storeId, storeOptions }) => {
  const label = () => {
    if (startTime != null && endTime != null) {
      // the startTime and endTime come as a number 900 or 1700, we need to tranform it as HH:mm am/pm
      const startTimeFormatted = format(new Date().setHours(Math.floor(startTime / 100), startTime % 100), 'h:mmaaa');
      const endTimeFormatted = format(new Date().setHours(Math.floor(endTime / 100), endTime % 100), 'h:mmaaa');
      return `${startTimeFormatted} - ${endTimeFormatted}`;
    }
    return 'Out of Office';
  };

  return (
    <div className="flex-row-spacebetween-center w-100-P m-top-10">
      <div className="flex-row-center gap-16">
        <span>{dayjs(date).format('MMMM DD, YYYY')}</span>
        <span>{label()}</span>
        <span>{storeOptions.find(option => option.value === storeId)?.label}</span>
      </div>
      <div className="flex-row-center gap-16">
        <Button
          onClick={() =>
            handleEdit({
              endTime,
              startTime,
              date,
              storeId
            })
          }
        >
          Edit
        </Button>
        <Popconfirm onConfirm={() => handleDelete(storeId, date)} title="Are you sure you want to delete this availability?">
          <Button danger>Delete</Button>
        </Popconfirm>
      </div>
    </div>
  );
};

const AssociateHoursItem = ({ label, day, storeOptions, form }) => {
  const enabled = Form.useWatch(['open_hours', day, 'enabled'], form);
  return (
    <Row>
      <Col span={3} flex={'auto'}>
        <Form.Item name={['open_hours', day, 'enabled']} style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-start' }} valuePropName="checked">
          <Checkbox>{label}</Checkbox>
        </Form.Item>
      </Col>
      <Col span={16}>
        <Form.Item className="w-100-P" name={['open_hours', day, 'hours']} style={{ marginBottom: 8 }}>
          <TimePicker.RangePicker
            disabled={!enabled}
            style={{ width: '75%' }}
            format="hh:mma"
            placeholder={['Open', 'Close']}
          />
        </Form.Item>
      </Col>
      <Col span={5}>
        <Form.Item name={['open_hours', day, 'storeId']} style={{ marginBottom: 8 }}>
          <Select popupMatchSelectWidth={false} disabled={!enabled} options={storeOptions} style={{ minWidth: '150px', maxWidth: '100%' }} placeholder={'Store'} />
        </Form.Item>
      </Col>
    </Row>
  );
};
